import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { Form, Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { policyTypes } from '@ourbranch/policy-types';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { FormField, FormSubmit } from 'core/components/form';
import { getMergedAllSegments } from 'customer/components/policy/merge-segments';
import {
  buildInterestedPartyFromForm,
  interestedPartyRelationshipOptions,
  isLossPayeeRelationship,
  usesBrandInterestedPartyTypes
} from 'common/helpers/auto-interested-party-helpers';
import { validationSchema } from './add-lien-holders.validation';
import LienHolderAutoComplete from './lien-holder-autocomplete';
import { getCarOptions } from './helper';
import useStyles from '../add-form.styles';

const associatedEntityOptions = ({ values, showRenters, policyDetails }) => {
  if (values.relationship === 'lessor') {
    return getCarOptions({ ...values, ...policyDetails }, false);
  }
  return getCarOptions({ ...values, ...policyDetails }, showRenters);
};

function AddLienHoldersForm({ onAdd, disabled }) {
  const classes = useStyles();
  const { values } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();

  const policyDetails = inPolicyRoute ? getMergedAllSegments(values.segments) : null;
  const state = values?.state || values?.correctedAddress?.state || values?.homeLocation?.state;

  const showRenters = inPolicyRoute
    ? policyDetails?.includeRenters || values?.policyType === policyTypes.Renters
    : !!values.rentersCoverage && !!values.includeRenters;

  // Created once per mount so a parent re-render (e.g. a background session refresh) can't reset what the
  // agent has typed. FormList remounts this form after each add (RepeatField key={list.length}), so every
  // added party still gets a fresh id.
  // #36264: a brand with typed interests (CFM) attaches one party to several vehicles and, for a
  // Trustee, records the trustee name(s) beside the trust's name.
  const brandTyped = usesBrandInterestedPartyTypes();
  const [initialValues] = useState(() => ({
    id: uuidv4(),
    name: '',
    loanNumber: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    VIN: '',
    VINs: [],
    relationship: '',
    trusteeNames: ''
  }));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(formValues) => onAdd(buildInterestedPartyFromForm(formValues))}
    >
      {({ setValues, values: lienHolderFormValues }) => (
        <Form disabled={disabled} className={classes.container}>
          <Grid
            key="container"
            container
            justifyContent="space-around"
            alignItems="flex-start"
            className={classes.formContainer}
            spacing={4}
          >
            <FormField
              mode="light"
              name="relationship"
              id="relationship"
              label="Relationship"
              type="select"
              options={interestedPartyRelationshipOptions({
                state,
                showRenters,
                standaloneRenters: values?.policyType === policyTypes.Renters
              })}
              xs={8}
              permissions={{ isLicensedAction: false }}
              fast={false}
            />
            {(
              brandTyped
                ? isLossPayeeRelationship(lienHolderFormValues.relationship)
                : lienHolderFormValues.relationship === 'lienholder'
            ) ? (
              <FormField
                mode="light"
                name="loanNumber"
                id="loanNumber"
                label="Loan Number (optional)"
                type="string"
                optional
                xs={4}
                permissions={{ isLicensedAction: false }}
              />
            ) : (
              <Grid item xs={4} />
            )}
            <LienHolderAutoComplete
              xs={12}
              setValues={setValues}
              currentValues={lienHolderFormValues}
              label={brandTyped && lienHolderFormValues.relationship === 'trustee' ? 'Trust Name' : undefined}
            />
            {brandTyped && lienHolderFormValues.relationship === 'trustee' && (
              <FormField
                mode="light"
                name="trusteeNames"
                id="trusteeNames"
                label="Trustee name(s), if other than the named insured"
                type="string"
                optional
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            )}
            <FormField
              mode="light"
              name="address"
              id="address"
              label="Address"
              type="address"
              xs={8}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="address2"
              id="address2"
              label="Address 2"
              type="string"
              optional
              xs={4}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="city"
              id="city"
              label="City"
              type="string"
              optional
              xs={4}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="state"
              id="state"
              label="State"
              type="state"
              optional
              xs={4}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="zip"
              id="zip"
              label="Zip code"
              type="string"
              optional
              xs={4}
              permissions={{ isLicensedAction: false }}
            />
            {brandTyped ? (
              <FormField
                mode="light"
                name="VINs"
                id="VINs"
                label="Vehicles"
                description="Select every vehicle this party has an interest in"
                type="chipArray"
                options={getCarOptions({ ...values, ...policyDetails }, false)}
                placeholder="Select vehicles"
                fast={false}
                xs={8}
                permissions={{ isLicensedAction: false }}
              />
            ) : (
              <FormField
                mode="light"
                name="VIN"
                id="VIN"
                label={
                  values.relationship === 'lessor'
                    ? 'Selected Associated Entity'
                    : 'Selected Associated Entity (optional)'
                }
                type="select"
                options={associatedEntityOptions({ values, showRenters, policyDetails })}
                optional
                fast={false}
                xs={8}
                permissions={{ isLicensedAction: false }}
              />
            )}
            <FormSubmit
              mode="big"
              className={classNames(classes.button, classes.addButton)}
              variant="contained"
              color="secondary"
              xs={4}
            >
              Add Interested Party
            </FormSubmit>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

AddLienHoldersForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};
AddLienHoldersForm.defaultProps = {
  disabled: false
};

export default AddLienHoldersForm;
